import { AlertTriangle } from 'lucide-react';

export interface FormulaWarning {
  scope: 'package' | 'quote';
  packageIndex?: number;
  chargeIndex: number;
  description: string | null;
  reason: string;
  rateBps?: number;
  basis?: unknown;
  computed?: number;
  stated: number;
}

export interface ChargeReviewBannerProps {
  warnings: FormulaWarning[];
  chargeIndexToId: Record<number, string>;
  onReviewClick: (chargeId: string) => void;
}

function formatDollars(cents: number): string {
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function formatPct(rateBps: number): string {
  return `${(rateBps / 100).toFixed(2).replace(/\.00$/, '')}%`;
}

interface WarningRowProps {
  warning: FormulaWarning;
  chargeId: string | undefined;
  onReviewClick: (chargeId: string) => void;
}

function WarningRow({ warning, chargeId, onReviewClick }: WarningRowProps) {
  const label = warning.description ?? 'Charge';

  if (warning.reason === 'round-amount-demoted') {
    return (
      <div className="flex items-start justify-between gap-3">
        <span>
          <span className="font-medium">{label}</span>
          {' '}looked like a percentage of subtotal but the{' '}
          <span className="font-medium">round amount</span>
          {' '}suggests flat. Confirmed flat — undo if wrong.
        </span>
        {chargeId && (
          <button
            type="button"
            onClick={() => onReviewClick(chargeId)}
            className="shrink-0 rounded px-2 py-0.5 text-[11px] font-medium ring-1 ring-amber-500/50 hover:bg-amber-500/20"
          >
            Mark as percentage
          </button>
        )}
      </div>
    );
  }

  if (warning.reason === 'stated-rate-drift') {
    const ratePct = warning.rateBps != null ? formatPct(warning.rateBps) : '?%';
    const statedFmt = formatDollars(warning.stated);
    const computedFmt = warning.computed != null ? formatDollars(warning.computed) : null;
    const discrepancy =
      warning.computed != null ? formatDollars(Math.abs(warning.computed - warning.stated)) : null;

    return (
      <div className="flex items-start justify-between gap-3">
        <span>
          <span className="font-medium">{label}</span>
          {': '}Vendor printed {statedFmt} for {ratePct} but{' '}
          {ratePct} × basis
          {computedFmt ? ` = ${computedFmt}` : ''}.
          {discrepancy ? ` Discrepancy: ${discrepancy}.` : ''}
        </span>
        {chargeId && (
          <button
            type="button"
            onClick={() => onReviewClick(chargeId)}
            className="shrink-0 rounded px-2 py-0.5 text-[11px] font-medium ring-1 ring-amber-500/50 hover:bg-amber-500/20"
          >
            Open formula editor
          </button>
        )}
      </div>
    );
  }

  if (warning.reason === 'non-tidy-rate') {
    const ratePct = warning.rateBps != null ? formatPct(warning.rateBps) : '?%';
    return (
      <div className="flex items-start justify-between gap-3">
        <span>
          Implied rate on <span className="font-medium">{label}</span> is{' '}
          <span className="font-medium">{ratePct}</span> — unusual. Verify the basis or set a manual rate.
        </span>
        {chargeId && (
          <button
            type="button"
            onClick={() => onReviewClick(chargeId)}
            className="shrink-0 rounded px-2 py-0.5 text-[11px] font-medium ring-1 ring-amber-500/50 hover:bg-amber-500/20"
          >
            Open formula editor
          </button>
        )}
      </div>
    );
  }

  // Generic fallback
  return (
    <div className="flex items-start justify-between gap-3">
      <span>
        <span className="font-medium">{label}</span>: {warning.reason}
      </span>
      {chargeId && (
        <button
          type="button"
          onClick={() => onReviewClick(chargeId)}
          className="shrink-0 rounded px-2 py-0.5 text-[11px] font-medium ring-1 ring-amber-500/50 hover:bg-amber-500/20"
        >
          Open
        </button>
      )}
    </div>
  );
}

export function ChargeReviewBanner({
  warnings,
  chargeIndexToId,
  onReviewClick,
}: ChargeReviewBannerProps) {
  const perCharge = warnings.filter((w) => w.chargeIndex >= 0);
  if (perCharge.length === 0) return null;

  return (
    <div className="space-y-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
      <div className="flex items-center gap-1.5 font-semibold">
        <AlertTriangle className="size-3.5 shrink-0" />
        <span>
          {perCharge.length === 1 ? '1 charge needs review' : `${perCharge.length} charges need review`}
        </span>
      </div>
      <div className="space-y-1.5 pl-5">
        {perCharge.map((w, idx) => (
          <WarningRow
            key={idx}
            warning={w}
            chargeId={chargeIndexToId[w.chargeIndex]}
            onReviewClick={onReviewClick}
          />
        ))}
      </div>
    </div>
  );
}
